import { createContext, type ReactNode, useContext } from 'react'
import { createPortal } from 'react-dom'

/* `undefined` outside a detail; `null` inside one whose lead sentence is not laid out yet. */
const AttentionIntroSlotContext = createContext<HTMLElement | null | undefined>(undefined)

interface IAttentionIntroSlotProviderProps {
  container: HTMLElement | null
  children: ReactNode
}

/**
 * Hands a status section the end of the detail's lead sentence. The sentence is the placeholder's,
 * while what closes it — a way to the section the status is about — is known by the section that
 * owns its handler, the same split the card corner makes for the actions.
 */
export const AttentionIntroSlotProvider = ({
  container,
  children,
}: IAttentionIntroSlotProviderProps) => (
  <AttentionIntroSlotContext.Provider value={container}>
    {children}
  </AttentionIntroSlotContext.Provider>
)

AttentionIntroSlotProvider.displayName = 'AttentionIntroSlotProvider'

/** Draws its children at the end of the lead sentence; outside a detail, where it stands. */
export const AttentionIntroEnd = ({ children }: { children: ReactNode }) => {
  const container = useContext(AttentionIntroSlotContext)

  if (container === undefined) {
    return <>{children}</>
  }

  return container ? createPortal(children, container) : null
}

AttentionIntroEnd.displayName = 'AttentionIntroEnd'

/* `undefined` outside a detail that sets the lead text in its own column; `null` while that column
   is not laid out yet. */
const AttentionIntroFootSlotContext = createContext<HTMLElement | null | undefined>(undefined)

/**
 * Hands a status section the place under the detail's lead text, where the text sits in a column
 * of its own: what the section says about its subject closes the text that explains it.
 */
export const AttentionIntroFootSlotProvider = ({
  container,
  children,
}: IAttentionIntroSlotProviderProps) => (
  <AttentionIntroFootSlotContext.Provider value={container}>
    {children}
  </AttentionIntroFootSlotContext.Provider>
)

AttentionIntroFootSlotProvider.displayName = 'AttentionIntroFootSlotProvider'

/** Draws its children under the lead text; outside such a detail, where it stands. */
export const AttentionIntroFoot = ({ children }: { children: ReactNode }) => {
  const container = useContext(AttentionIntroFootSlotContext)

  if (container === undefined) {
    return <>{children}</>
  }

  return container ? createPortal(children, container) : null
}

AttentionIntroFoot.displayName = 'AttentionIntroFoot'

/* `undefined` outside a detail that sets the lead text in its own column; `null` while that column
   is not laid out yet. */
const AttentionIntroHeadSlotContext = createContext<HTMLElement | null | undefined>(undefined)

/**
 * Hands a status section the top of the detail's text column, above the lead text: what the reader
 * acts on first — who the status waits for — before the text that explains it.
 */
export const AttentionIntroHeadSlotProvider = ({
  container,
  children,
}: IAttentionIntroSlotProviderProps) => (
  <AttentionIntroHeadSlotContext.Provider value={container}>
    {children}
  </AttentionIntroHeadSlotContext.Provider>
)

AttentionIntroHeadSlotProvider.displayName = 'AttentionIntroHeadSlotProvider'

/** Draws its children above the lead text; outside such a detail, where it stands. */
export const AttentionIntroHead = ({ children }: { children: ReactNode }) => {
  const container = useContext(AttentionIntroHeadSlotContext)

  if (container === undefined) {
    return <>{children}</>
  }

  return container ? createPortal(children, container) : null
}

AttentionIntroHead.displayName = 'AttentionIntroHead'
